import { CSSProperties } from "react";

import { useToken } from "@chakra-ui/react";

/**
 * Hook to get editor styles using Chakra UI theme
 * This ensures the editor always matches the global theme while maintaining inline styles
 */
export const useEditorStyles = () => {
  const [gray300, gray700, gray400, gray50, gray100, white, blue500, red600] =
    useToken("colors", [
      "gray.300",
      "gray.700",
      "gray.400",
      "gray.50",
      "gray.100",
      "white",
      "blue.500",
      "red.600"
    ]);
  const [bodyFont] = useToken("fonts", ["body"]);
  const [fontSizeSm] = useToken("fontSizes", ["sm"]);
  const [lineHeightBase] = useToken("lineHeights", ["base"]);
  const [radiiMd, radiiSm] = useToken("radii", ["md", "sm"]);
  const [space1, space2, space3, space1_5, space2_5] = useToken("spacing", [
    "1",
    "2",
    "3",
    "1.5",
    "2.5"
  ]);
  // Must be the `borders` shorthand ("1px solid"), not a width: a bare width
  // leaves `border-style` unset, so the border renders as none. `borders.sm` is
  // v3's equivalent of v2's `borders["1px"]`.
  const [borderThin] = useToken("borders", ["sm"]);
  const [shadowOutline] = useToken("shadows", ["outline"]);
  const [size8, size6] = useToken("sizes", ["8", "6"]);

  const colors = {
    gray: {
      300: gray300,
      700: gray700,
      400: gray400,
      50: gray50,
      100: gray100
    },
    white,
    blue: { 500: blue500 },
    red: { 600: red600 }
  };

  return {
    defaultEditorStyles: {
      position: "relative",
      minHeight: "150px",
      maxHeight: "400px",
      overflowY: "auto",
      padding: space3,
      border: `${borderThin} ${colors.gray[300]}`,
      borderRadius: radiiMd,
      fontSize: fontSizeSm,
      lineHeight: lineHeightBase,
      color: colors.gray[700],
      backgroundColor: colors.white,
      outline: "none",
      fontFamily: bodyFont
    } as CSSProperties,

    focusedEditorStyles: {
      borderWidth: "2px",
      borderColor: colors.blue[500],
      boxShadow: shadowOutline,
      padding: "11px"
    } as CSSProperties,

    disabledEditorStyles: {
      backgroundColor: colors.gray[50],
      color: colors.gray[400],
      cursor: "not-allowed"
    } as CSSProperties,

    invalidEditorStyles: {
      borderWidth: "2px",
      borderColor: colors.red[600],
      boxShadow: `0 0 0 1px ${colors.red[600]}`,
      padding: "11px"
    } as CSSProperties,

    defaultToolbarStyles: {
      display: "flex",
      flexWrap: "wrap",
      gap: space1,
      padding: space2,
      borderBottom: `${borderThin} ${colors.gray[300]}`,
      backgroundColor: colors.gray[50],
      borderTopLeftRadius: radiiMd,
      borderTopRightRadius: radiiMd
    } as CSSProperties,

    toolbarButtonStyles: {
      padding: `${space1_5} ${space2_5}`,
      border: `${borderThin} ${colors.gray[300]}`,
      borderRadius: radiiSm,
      backgroundColor: colors.white,
      cursor: "pointer",
      fontSize: fontSizeSm,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: colors.gray[700],
      outline: "none",
      minWidth: size8,
      height: size8
    } as CSSProperties,

    toolbarButtonHoverStyles: {
      backgroundColor: colors.gray[100],
      borderColor: colors.gray[300]
    } as CSSProperties,

    toolbarButtonFocusStyles: {
      borderColor: colors.blue[500],
      boxShadow: shadowOutline,
      outline: "none"
    } as CSSProperties,

    toolbarButtonActiveStyles: {
      backgroundColor: colors.blue[500],
      color: colors.white,
      borderColor: colors.blue[500]
    } as CSSProperties,

    toolbarButtonDisabledStyles: {
      cursor: "not-allowed",
      pointerEvents: "none"
    } as CSSProperties,

    toolbarDividerStyles: {
      width: "1px",
      height: size6,
      backgroundColor: colors.gray[300],
      margin: `0 ${space1}`
    } as CSSProperties
  };
};
